import type { Dictionary, EntityState, PayloadAction } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import type { IProduct, IProductHeader } from '~/Slices/schema';
import { productHeaderId } from '~/Slices/schema';
import { ApiRequestError, ApiServerError, fetch2, unwrapFetch2 } from '~/fetch';
import { endpoint } from '~/helpers/Endpoints';
import type { IAppState } from '~/appstate';
import type * as Values from '~/values';
import * as Hub from '../ogm-hub';
import { fetchMetaProduct } from './MetaProductsSlice';
import * as ProductLineSlice from './ProductLinesSlice';

export type IProductHeadersState = EntityState<IProductHeader>;

export const productHeadersAdapter = createEntityAdapter<IProductHeader>({
	selectId: productHeaderId,
});

export interface FetchSearchResultsParams {
	searchTerm: string;
	blueprintId?: number;
	pageIndex: number;
	pageLimit: number;
	corpSupplierCodesFilter: string[];
	distributionCentersFilter: string[];
	categoryLevel1Filter: string;
	categoryLevel2Filter: string;
	categoryLevel3Filter: string;
	categoryLevel4Filter: string;
	brandFilter: string;
	packSizeFilter: string[];
	allowedStockTypes: Hub.StockType[];
	includeOnlyEligibleItems?: boolean;
}

export interface FetchSearchResults {
	currentPage: IProduct[];
	productHeaders: IProductHeader[];
	totalPages: number;
	totalResults: number;
	supplierFacet: Hub.SearchFacet[];
	categoryLevel1Facet: Hub.SearchFacet[];
	categoryLevel2Facet: Hub.SearchFacet[];
	categoryLevel3Facet: Hub.SearchFacet[];
	categoryLevel4Facet: Hub.SearchFacet[];
	brandFacet: Hub.SearchFacet[];
	packSizeFacet: Hub.SearchFacet[];
}

export interface FetchRecommendationsParams {
	blueprintId: number;
	productIdentifiers: {
		corpSupplierCode: string;
		supplierProductNumber: string;
	}[];
}

export interface IFetchGLGroupsForProductParms {
	corpSupplierCode: string;
	supplierProductNumber: string;
	distributionCenterCodes: string[];
}

export interface IFetchGLGroupsForProductsResult {
	corpSupplierCode: string;
	supplierProductNumber: string;
	glGroup: Hub.GlGroup;
}

let fetchSearchResultsPromise;
const fetchSearchResultsInternal = createAsyncThunk<
	Hub.SearchResult,
	FetchSearchResultsParams
>(
	'productHeaders/fetchSearchResultsInternal',
	(params: FetchSearchResultsParams) => {
		const requestArgs: Hub.SearchRequestObject = {
			searchTerm: params.searchTerm?.trim(),
			blueprintId: params.blueprintId,
			skip: params.pageIndex * params.pageLimit,
			limit: params.pageLimit,
			corpSupplierCodesFilter: params.corpSupplierCodesFilter,
			distributionCentersFilter: params.distributionCentersFilter,
			brandFilter: params.brandFilter,
			packSizeFilter: null,
			packSizeFilters: params.packSizeFilter,
			categoryLevel1Filter: params.categoryLevel1Filter,
			categoryLevel2Filter: params.categoryLevel2Filter,
			categoryLevel3Filter: params.categoryLevel3Filter,
			categoryLevel4Filter: params.categoryLevel4Filter,
			allowedStockTypes: params.allowedStockTypes,
			includeOnlyEligibleItems: params.includeOnlyEligibleItems || false,
		};
		return unwrapFetch2(Hub.SearchController.Search(requestArgs));
	}
);

export const fetchSearchResults = createAsyncThunk<
	FetchSearchResults,
	FetchSearchResultsParams,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'productHeaders/fetchSearchResults',
	async (params: FetchSearchResultsParams, { dispatch, rejectWithValue }) => {
		try {
			if (fetchSearchResultsPromise) {
				fetchSearchResultsPromise.abort();
			}
			fetchSearchResultsPromise = dispatch(fetchSearchResultsInternal(params));
			const promiseResult = await fetchSearchResultsPromise;
			if (promiseResult.error) {
				throw promiseResult.error;
			}
			const { payload } = promiseResult;

			const headers = [];
			const currentPage = [];

			payload.results.results.forEach((product) => {
				const mappedProduct: IProductHeader = { ...product };
				const result = {
					corpSupplierCode: mappedProduct.corpSupplierCode,
					supplierProductNumber: mappedProduct.supplierProductNumber,
					unitMeasureCode: null,
				};
				currentPage.push(result);
				headers.push(mappedProduct);
			});

			return {
				currentPage,
				productHeaders: headers,
				totalPages: payload.results.totalPages,
				totalResults: payload.results.totalResults,
				supplierFacet: payload.supplierFacet,
				categoryLevel1Facet: payload.categoryLevel1Facet,
				categoryLevel2Facet: payload.categoryLevel2Facet,
				categoryLevel3Facet: payload.categoryLevel3Facet,
				categoryLevel4Facet: payload.categoryLevel4Facet,
				brandFacet: payload.brandFacet,
				packSizeFacet: payload.packSizeFacet,
			};
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const sortPackSizeFacet = (options: Hub.SearchFacet[]) => {
	if (options && options.length > 1) {
		const comparator = (a: Hub.SearchFacet, b: Hub.SearchFacet) => {
			const regex = /([#A-Za-z]*)/g;
			const getUnit = (packSize: string) =>
				packSize
					.split(' ')
					.join('')
					.match(regex)
					?.find((x) => x != '');
			const aunit = getUnit(a.displayString);
			const bunit = getUnit(b.displayString);
			if (aunit < bunit) {
				return -1;
			}
			if (aunit > bunit) {
				return 1;
			}
			return a.displayString.localeCompare(b.displayString, 'en', {
				numeric: true,
			});
		};
		return options.sort(comparator);
	}
	return options;
};

export interface FetchProductsByProductNumberParams {
	blueprintId: number | string;
	productNumbers: string[];
}
export const fetchProductsByProductNumber = createAsyncThunk<
	IProductHeader[],
	FetchProductsByProductNumberParams,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'productHeaders/fetchProductsByProductNumber',
	async (params: FetchProductsByProductNumberParams, { rejectWithValue }) => {
		try {
			const result = await unwrapFetch2(
				fetch2<Dictionary<Values.IProductHeader>>({
					uri: endpoint.lookUpProducts({ bpid: params.blueprintId }),
					method: 'POST',
					body: JSON.stringify(params.productNumbers),
				})
			);
			// NOTE: Result is a dictionary. Values in that dictionary CAN be null
			return Object.values(result)
				.filter((r) => r)
				.map((product) => {
					return {
						...(product as any),
						supplierCategoryLevel1Code: product.categoryLevel1Code,
						supplierCategoryLevel2Code: product.categoryLevel2Code,
						supplierCategoryLevel3Code: product.categoryLevel3Code,
						supplierCategoryLevel4Code: product.categoryLevel4Code,
						supplierCategoryLevel1Description: product.categoryLevel1,
						supplierCategoryLevel2Description: product.categoryLevel2,
						supplierCategoryLevel3Description: product.categoryLevel3,
						supplierCategoryLevel4Description: product.categoryLevel4,
					} as IProductHeader;
				});
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface fetchProductsByProductNumberAnyStatusParams {
	supplierId: number;
	productNumbers: string[];
}
export const fetchProductsByProductNumberAnyStatus = createAsyncThunk<
	IProductHeader[],
	fetchProductsByProductNumberAnyStatusParams,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'productHeaders/fetchProductsByProductNumberAnyStatus',
	async (
		params: fetchProductsByProductNumberAnyStatusParams,
		{ rejectWithValue }
	) => {
		try {
			const result = await unwrapFetch2(
				Hub.ProductController.GetProductsByProductNumbersAnyStatus(
					params.supplierId,
					params.productNumbers
				)
			);
			const { results } = result;
			const headers = [];

			results.forEach((product) => {
				const mappedProduct: IProductHeader = {
					...product,
					productAlertDcs: null,
					productNetUnits: [],
				};
				mappedProduct.id = productHeaderId(mappedProduct);
				headers.push(mappedProduct);
			});
			// NOTE: Result is a dictionary. Values in that dictionary CAN be null
			return headers;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const fetchGLGroups = createAsyncThunk<
	IFetchGLGroupsForProductsResult[],
	IFetchGLGroupsForProductParms[],
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'productHeaders/fetchGLGroups',
	async (params: IFetchGLGroupsForProductParms[], { rejectWithValue }) => {
		try {
			const productsWithDcs: Hub.ProductWithDcs[] = params.map((x) => ({
				productNumber: x.supplierProductNumber,
				distributionCenters: x.distributionCenterCodes,
			}));
			const result = await unwrapFetch2(
				Hub.GeneralLedgerController.GetGlGroupsForProducts({ productsWithDcs })
			);

			return params.map((product) => ({
				corpSupplierCode: product.corpSupplierCode,
				supplierProductNumber: product.supplierProductNumber,
				glGroup: result.glGroups.find(
					(glGroup) => glGroup.productNumber === product.supplierProductNumber
				).glGroup,
			}));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const fetchRecommendations = createAsyncThunk<
	IProductHeader[],
	FetchRecommendationsParams,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'productHeaders/loadRecommendations',
	async (params: FetchRecommendationsParams, { rejectWithValue }) => {
		try {
			const body = JSON.stringify(params.productIdentifiers);
			const result = await unwrapFetch2(
				fetch2({
					uri: endpoint.recommendations({
						masterOrderGuideId: params.blueprintId,
					}),
					method: 'POST',
					body,
				})
			);

			return result as IProductHeader[];
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const productHeadersSlice = createSlice({
	name: 'productHeaders',
	initialState: productHeadersAdapter.getInitialState(),
	reducers: {
		setGlGroup(
			state,
			action: PayloadAction<{ id: string; glGroup: Hub.GlGroup }>
		) {
			const { id, glGroup } = action.payload;
			state.entities[id].glGroup = glGroup;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchMetaProduct.fulfilled, (state, action) => {
			if (action.payload.entities.productHeaders) {
				productHeadersAdapter.upsertMany(
					state,
					action.payload.entities.productHeaders
				);
			}
		});
		builder.addCase(fetchSearchResults.fulfilled, (state, action) => {
			productHeadersAdapter.upsertMany(state, action.payload.productHeaders);
		});
		builder.addCase(fetchRecommendations.fulfilled, (state, action) => {
			productHeadersAdapter.upsertMany(state, action.payload);
		});
		builder.addCase(fetchGLGroups.fulfilled, (state, action) => {
			action.payload.forEach((product) => {
				const id = `${product.corpSupplierCode}|${product.supplierProductNumber}`;
				if (state.entities[id] != null) {
					state.entities[id].glGroup = product.glGroup;
				}
			});
		});

		builder.addCase(fetchProductsByProductNumber.fulfilled, (state, action) => {
			productHeadersAdapter.upsertMany(state, action.payload);
		});

		builder.addCase(
			fetchProductsByProductNumberAnyStatus.fulfilled,
			(state, action) => {
				productHeadersAdapter.upsertMany(state, action.payload);
			}
		);

		builder.addCase(
			ProductLineSlice.fetchProductLines.fulfilled,
			(state, action) => {
				const lines = Object.values(action.payload.entities.productLines);
				const phaseOutHeaders = lines
					.filter((l: any) => l.phaseOutReplacementProductHeader)
					.map((l: any) => l.phaseOutReplacementProductHeader);
				productHeadersAdapter.upsertMany(state, phaseOutHeaders);
			}
		);
	},
});

export const { setGlGroup } = productHeadersSlice.actions;

export function selectProductHeaders(state, ids: string[]): IProductHeader[] {
	return ids.map((id) => selectProductHeader(state, id));
}

export function selectProductHeader(state, id: string): IProductHeader {
	return state.productHeaders.entities[id];
}
